import {
  LEADERBOARD_MAX_SCORE,
  LEADERBOARD_DEFAULT_LIMIT,
} from "./constants.js";
import {
  PLAYER_NAME_STORAGE_KEY,
  StorageSlot,
  readStorageRecord,
  writeStorageRecord,
} from "../core/storage.js";

export { PLAYER_NAME_STORAGE_KEY };
export const LEADERBOARD_MAX_NAME_LENGTH = 24;
export { LEADERBOARD_MAX_SCORE };
export { LEADERBOARD_DEFAULT_LIMIT };

function isTestMode() {
  return typeof import.meta !== "undefined" && import.meta.env && import.meta.env.MODE === "test";
}

function isBrowser() {
  return typeof window !== "undefined";
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function normalizeLeaderboardScore(value) {
  var score = Math.floor(Number(value) || 0);
  if (!isFinite(score) || score < 0) score = 0;
  if (score > LEADERBOARD_MAX_SCORE) score = LEADERBOARD_MAX_SCORE;
  return score;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function sanitizeLeaderboardName(value) {
  var name = typeof value === "string" ? value.trim() : "";
  if (!name) return "Scientist";
  if (name.length > LEADERBOARD_MAX_NAME_LENGTH) {
    name = name.slice(0, LEADERBOARD_MAX_NAME_LENGTH);
  }
  return name;
}

/**
 * @param {string | null | undefined} crazygamesUsername
 * @param {{ storage?: Storage | null, y8Nickname?: string | null }} [opts]
 * @returns {string}
 */
export function getPlayerDisplayName(crazygamesUsername, opts) {
  var y8Name = opts && typeof opts.y8Nickname === "string" ? opts.y8Nickname.trim() : "";
  if (y8Name) return sanitizeLeaderboardName(y8Name);

  var portalName = typeof crazygamesUsername === "string" ? crazygamesUsername.trim() : "";
  if (portalName) return sanitizeLeaderboardName(portalName);

  var storage = (opts && opts.storage) || (isBrowser() ? window.localStorage : null);
  if (storage) {
    var stored = readStorageRecord(storage, StorageSlot.PLAYER_NAME);
    if (typeof stored === "string" && stored) return sanitizeLeaderboardName(stored);
  }

  var generated = "Scientist #" + String(Math.floor(1000 + Math.random() * 9000));
  if (storage) {
    writeStorageRecord(storage, StorageSlot.PLAYER_NAME, generated);
  }
  return generated;
}

/**
 * @param {{ storage?: Storage | null }} [opts]
 * @returns {string | null}
 */
export function getPlayerId(opts) {
  var storage = (opts && opts.storage) || (isBrowser() ? window.localStorage : null);
  if (storage) {
    var stored = readStorageRecord(storage, StorageSlot.PLAYER_ID);
    if (typeof stored === "string" && stored) return stored;
  }

  if (typeof crypto === "undefined" || typeof crypto.randomUUID !== "function") {
    return null;
  }

  var playerId = crypto.randomUUID();
  if (storage) {
    writeStorageRecord(storage, StorageSlot.PLAYER_ID, playerId);
  }
  return playerId;
}

/**
 * @param {{ storage?: Storage | null }} [opts]
 * @returns {Promise<string | null>}
 */
export function ensurePlayerId(opts) {
  if (!isBrowser() || isTestMode()) return Promise.resolve(null);
  return Promise.resolve(getPlayerId(opts));
}

/** @deprecated Use ensurePlayerId — kept for callers expecting async auth-shaped API. */
export function ensureLeaderboardAuth(opts) {
  return ensurePlayerId(opts);
}

/**
 * @param {string} entryId
 * @returns {string}
 */
export function extractUidFromEntryId(entryId) {
  if (!entryId || typeof entryId !== "string") return "";
  var idx = entryId.lastIndexOf("_");
  if (idx <= 0) return entryId;
  return entryId.slice(0, idx);
}

/**
 * @param {string} id
 * @param {{ name?: string, score?: number, createdAt?: number, runIndex?: number }} raw
 * @returns {{ id: string, name: string, score: number, uid: string, createdAt: number, runIndex?: number }}
 */
export function normalizeLeaderboardEntry(id, raw) {
  return {
    id: id,
    name: sanitizeLeaderboardName(raw && raw.name),
    score: normalizeLeaderboardScore(raw && raw.score),
    uid: extractUidFromEntryId(id),
    createdAt: Math.max(0, Math.floor(Number(raw && raw.createdAt) || 0)),
    runIndex: raw && raw.runIndex != null
      ? Math.max(0, Math.floor(Number(raw.runIndex) || 0))
      : undefined,
  };
}

/**
 * @param {Array<{ id: string, name: string, score: number, uid?: string, createdAt?: number }>} topEntries
 * @param {{ id: string, name: string, score: number, uid?: string, createdAt?: number } | null | undefined} ownEntry
 * @param {number} [limit]
 * @returns {{ rows: Array<{ id: string, name: string, score: number, uid: string, createdAt: number }>, unrankedOwnRow: { id: string, name: string, score: number, uid: string, createdAt: number } | null }}
 */
export function mergeOwnEntry(topEntries, ownEntry, limit) {
  var maxRows = limit != null ? Math.max(1, Math.floor(Number(limit) || LEADERBOARD_DEFAULT_LIMIT)) : LEADERBOARD_DEFAULT_LIMIT;
  var combined = Array.isArray(topEntries) ? topEntries.slice() : [];
  if (ownEntry && ownEntry.id) {
    var hasOwn = combined.some(function (row) { return row.id === ownEntry.id; });
    if (!hasOwn) {
      combined.push(normalizeLeaderboardEntry(ownEntry.id, ownEntry));
    } else {
      combined = combined.map(function (row) {
        if (row.id !== ownEntry.id) return normalizeLeaderboardEntry(row.id, row);
        var keptScore = Math.max(
          normalizeLeaderboardScore(row.score),
          normalizeLeaderboardScore(ownEntry.score)
        );
        return normalizeLeaderboardEntry(ownEntry.id, Object.assign({}, ownEntry, { score: keptScore }));
      });
    }
  }

  combined.sort(function (a, b) {
    if (b.score !== a.score) return b.score - a.score;
    return (b.createdAt || 0) - (a.createdAt || 0);
  });

  var rows = combined.slice(0, maxRows).map(function (row) {
    return normalizeLeaderboardEntry(row.id, row);
  });

  var unrankedOwnRow = null;
  if (ownEntry && ownEntry.id) {
    var inTop = rows.some(function (row) { return row.id === ownEntry.id; });
    if (!inTop) {
      unrankedOwnRow = normalizeLeaderboardEntry(ownEntry.id, ownEntry);
    }
  }

  return { rows: rows, unrankedOwnRow: unrankedOwnRow };
}

/**
 * @param {Record<string, unknown> | null | undefined} snapshot
 * @returns {Array<{ id: string, name: string, score: number, uid: string, createdAt: number }>}
 */
export function parseLeaderboardSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return [];
  var rows = [];
  for (var id in snapshot) {
    if (!Object.prototype.hasOwnProperty.call(snapshot, id)) continue;
    var raw = snapshot[id];
    if (!raw || typeof raw !== "object") continue;
    rows.push(normalizeLeaderboardEntry(id, /** @type {{ name?: string, score?: number, createdAt?: number, runIndex?: number }} */ (raw)));
  }
  rows.sort(function (a, b) {
    if (b.score !== a.score) return b.score - a.score;
    return (b.createdAt || 0) - (a.createdAt || 0);
  });
  return rows;
}

/**
 * Normalize callable `rows` (or a legacy top-node map) into sorted leaderboard entries.
 * @param {unknown} value
 * @returns {Array<{ id: string, name: string, score: number, uid: string, createdAt: number, runIndex?: number }>}
 */
export function normalizeLeaderboardRows(value) {
  if (Array.isArray(value)) {
    var rows = [];
    for (var i = 0; i < value.length; i++) {
      var raw = value[i];
      if (!raw || typeof raw !== "object") continue;
      var id = typeof raw.id === "string" && raw.id
        ? raw.id
        : (typeof raw.uid === "string" ? raw.uid : "");
      if (!id) continue;
      rows.push(normalizeLeaderboardEntry(id, raw));
    }
    rows.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
    return rows;
  }
  return parseLeaderboardSnapshot(/** @type {Record<string, unknown> | null | undefined} */ (value));
}

export { isBrowser, isTestMode };
